/**
 * Reiter „Überblick" der Kundenansicht: das laufende Projekt, die Wege zum
 * Kunden und die Aufgaben — das, was man beim Öffnen eines Kunden meistens sucht.
 * Alles Weitere steht unter „Daten" und „Verlauf" (Entwurf B, 03.11.2026).
 */
import { Link } from "react-router-dom";
import type { Kunde } from "../../lib/types";
import type { KundeProjektRow } from "../../hooks/usePlanung";
import { denormAddr, mapsUrl, type KForm } from "../../lib/kundeFormular";
import { statusTon, statusWort } from "../../lib/projekte";
import NotizenBlock from "../notizen/NotizenBlock";
import { MailKnopf, TelefonLink, kundeHauptnummer, kundeMailZiele } from "./KontaktAktionen";
import { Gruppe, Zeile } from "./KundeDaten";
import type { DatenBereich } from "./KundeDatenBlatt";
import { ZeichenMail, ZeichenOrt, ZeichenTelefon, ZeichenWeiter } from "./KundeZeichen";

type Props = {
  kunde: Kunde;
  stand: KForm;
  laufend: KundeProjektRow[];
  onBearbeiten: (b: DatenBereich) => void;
  onAlleProjekte: () => void;
};

export default function KundeUeberblick({ kunde, stand, laufend, onBearbeiten, onAlleProjekte }: Props) {
  const nummer = kundeHauptnummer(kunde);
  const ziele = kundeMailZiele(kunde);
  const liefer = denormAddr(stand.strasse, stand.plz, stand.ort).replace(/\n/g, ", ");

  return (
    <div className="ku-stapel">
      {laufend.slice(0, 2).map((p) => (
        <Link key={p.id} to={`/projekte/${p.id}`} className="ku-projekt-karte">
          <span className={`pk-status-punkt is-${statusTon(p.status)}`} aria-hidden="true" />
          <span className="ku-projekt-text">
            <span className="ku-projekt-titel">{p.title || `Projekt ${p.id}`}</span>
            <span className="ku-projekt-status">{statusWort(p.status)}</span>
          </span>
          <ZeichenWeiter />
        </Link>
      ))}
      {laufend.length > 2 && (
        <button type="button" className="ku-mehr" onClick={onAlleProjekte}>Alle {laufend.length} laufenden Projekte</button>
      )}

      <Gruppe titel="Kontakt" bereich="kontakt" onBearbeiten={onBearbeiten}>
        {!nummer && !ziele.length && !liefer && <p className="ku-leer">Noch nichts hinterlegt.</p>}
        {nummer && (
          <Zeile zeichen={<ZeichenTelefon />} wert={nummer} info={stand.kommunikationskanal === "telefon" ? "bevorzugt" : undefined}
            tat={<TelefonLink nummer={nummer} className="ku-rund"><ZeichenTelefon /></TelefonLink>} />
        )}
        {ziele[0] && (
          <Zeile zeichen={<ZeichenMail />} wert={ziele[0].email} info={stand.kommunikationskanal === "mail" ? "bevorzugt" : undefined}
            tat={<MailKnopf email={ziele[0].email} kundeId={kunde.id} kundeName={kunde.name} ziele={ziele} className="ku-rund"><ZeichenMail /></MailKnopf>} />
        )}
        {liefer && <Zeile zeichen={<ZeichenOrt />} wert={<a href={mapsUrl(liefer)} target="_blank" rel="noreferrer">{liefer}</a>} />}
      </Gruppe>

      <section className="ku-gruppe-karte" id="ku-aufgaben" aria-label="Aufgaben und Notizen">
        <div className="ku-gruppe-kopf">
          <h3>Aufgaben &amp; Notizen</h3>
          <Link className="ku-link" to="/aufgaben">Alle Aufgaben</Link>
        </div>
        <NotizenBlock bezug={{ kunde_id: kunde.id }} ohneKopf platzhalter="Was ist zu tun? z. B. „Rückruf wegen Termin“" />
      </section>
    </div>
  );
}
